import { useRef, useMemo, useCallback, useEffect } from 'react'
import JoditEditor from 'jodit-react'

interface NoteEditorProps {
  content: string
  onChange: (content: string) => void
  onSave?: () => void
  placeholder?: string
  height?: string | number
  readonly?: boolean
}

export default function NoteEditor({
  content,
  onChange,
  onSave,
  placeholder = 'Start writing your note...',
  height = 'calc(100vh - 220px)',
  readonly = false,
}: NoteEditorProps) {
  const editorRef = useRef<any>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Jodit configuration
  const config = useMemo(() => ({
    readonly: readonly,
    placeholder: placeholder,
    height: height,
    minHeight: 300,

    // Toolbar configuration
    toolbarAdaptive: true,
    toolbarSticky: true,
    toolbarStickyOffset: 56, // Header height

    // Buttons configuration - similar to existing summernote setup
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'ul',
      'ol',
      '|',
      'font',
      'fontsize',
      'brush',
      'paragraph',
      '|',
      'link',
      'image',
      'video',
      'table',
      '|',
      'align',
      '|',
      'undo',
      'redo',
      '|',
      'hr',
      'eraser',
      'copyformat',
      '|',
      'symbol',
      'fullsize',
      'print',
      'source',
    ],

    // Mobile buttons - simplified for touch
    buttonsMD: [
      'bold',
      'italic',
      '|',
      'ul',
      'ol',
      '|',
      'link',
      'image',
      '|',
      'undo',
      'redo',
      '|',
      'dots',
    ],

    buttonsSM: [
      'bold',
      'italic',
      '|',
      'ul',
      '|',
      'link',
      '|',
      'dots',
    ],

    buttonsXS: [
      'bold',
      'italic',
      'link',
      'dots',
    ],

    // Theme and styling
    theme: 'default',
    style: {
      font: '16px Arial, sans-serif',
      color: '#1f2937',
    },

    // Editor behavior
    spellcheck: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html' as const,

    // Enter key behavior
    enter: 'p' as const,

    // Image handling
    imageDefaultWidth: 300,

    // Link handling
    link: {
      followOnDblClick: true,
      processVideoLink: true,
      processPastedLink: true,
      noFollowCheckbox: false,
      openInNewTabCheckbox: true,
    },

    // Table handling
    table: {
      selectionCellStyle: 'border: 1px double #9c27b0;',
    },

    // Colors - matching app theme
    colors: {
      greyscale: ['#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF'],
      palette: ['#9c27b0', '#7b1fa2', '#e91e63', '#f44336', '#ff9800', '#ffeb3b', '#4caf50', '#2196f3', '#3f51b5', '#673ab7'],
      full: [
        '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff',
        '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff',
        '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc',
        '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599', '#b6d7a8', '#a2c4c9', '#a4c2f4', '#9fc5e8', '#b4a7d6', '#d5a6bd',
        '#cc4125', '#e06666', '#f6b26b', '#ffd966', '#93c47d', '#76a5af', '#6d9eeb', '#6fa8dc', '#8e7cc3', '#c27ba0',
        '#a61c00', '#cc0000', '#e69138', '#f1c232', '#6aa84f', '#45818e', '#3c78d8', '#3d85c6', '#674ea7', '#a64d79',
        '#85200c', '#990000', '#b45f06', '#bf9000', '#38761d', '#134f5c', '#1155cc', '#0b5394', '#351c75', '#741b47',
        '#5b0f00', '#660000', '#783f04', '#7f6000', '#274e13', '#0c343d', '#1c4587', '#073763', '#20124d', '#4c1130',
      ],
    },

    // Events
    events: {
      afterInit: (editor: any) => {
        editorRef.current = editor
      },
    },

    // i18n - can be extended
    language: 'en',

    // Disable some features for cleaner experience
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,

    // Clean HTML on paste
    cleanHTML: {
      fillEmptyParagraph: false,
      removeEmptyElements: false,
    },

    // Size constraints
    sizeLG: 900,
    sizeMD: 700,
    sizeSM: 400,

  }), [readonly, placeholder, height])

  // Debounced content change handler (auto-save after 2 seconds of inactivity)
  const handleChange = useCallback((newContent: string) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Call onChange immediately for UI updates
    onChange(newContent)

    // Set debounced auto-save
    if (onSave) {
      debounceTimerRef.current = setTimeout(() => {
        onSave()
      }, 2000) // 2 second debounce
    }
  }, [onChange, onSave])

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Handle Ctrl+S for manual save
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (onSave) {
          // Clear debounce timer and save immediately
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
          }
          onSave()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onSave])

  return (
    <div className="note-editor-container">
      <JoditEditor
        ref={editorRef}
        value={content}
        config={config}
        onBlur={handleChange}
        onChange={handleChange}
      />
    </div>
  )
}
